import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import { fetchExposureGroupById } from "./EG.helper";
import { httpPatch, httpPost } from "../../../../../Utils/apis/apis";
import { setToast } from "../../../../../store/actions/toastActions";
import { toastSeverity } from "../../../../../Utils/config/toastConfig";
import { STRINGS } from "./EG.constants";

const useStyles = makeStyles((theme) => ({
  sectionLabel: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: "var(--color-primary-text)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    "&:first-child": {
      marginTop: 0,
    },
  },
  sectionCard: {
    padding: 0,
    backgroundColor: "transparent",
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "calc(12px * var(--app-radius-scale, 1))",
      fontSize: "0.8125rem",
      backgroundColor: "var(--color-background-default)",
      "& fieldset": {
        borderColor: "var(--total_row_boldbackground)",
      },
      "&:hover fieldset": {
        borderColor: "var(--color-primary-main)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--color-primary-main)",
        borderWidth: 1.5,
      },
      "&.Mui-disabled": {
        backgroundColor: "var(--total_row_background)",
        "& fieldset": {
          borderColor: "var(--total_row_boldbackground)",
          borderStyle: "dashed",
        },
        "& .MuiOutlinedInput-input": {
          color: "var(--color-text-secondary)",
          WebkitTextFillColor: "var(--color-text-secondary)",
        },
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.8125rem",
      color: "var(--color-text-secondary)",
      transform: "translate(14px, 10px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
      },
      "&.Mui-disabled": {
        color: "var(--color-text-secondary)",
        opacity: 0.7,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      color: "var(--color-text-primary)",
    },
    "& .MuiOutlinedInput-inputMultiline": {
      resize: "vertical",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.6875rem",
      marginLeft: 2,
    },
  },
  embeddedActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const EMPTY = {
  name: "",
  description: "",
  notes: "",
};

function EGEditDialog({ groupId, groupName, open, onClose, onSaved, isEdit, embedded }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData(EMPTY);
    setErrors({});
    if (!isEdit || !groupId) return;

    let cancelled = false;
    setIsLoading(true);
    fetchExposureGroupById(groupId)
      .then((data) => {
        if (cancelled) return;
        setFormData({
          description: data.description || "",
          notes:       data.notes || "",
        });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [open, groupId, isEdit]);

  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  }, [errors]);

  const handleSave = useCallback(async () => {
    const newErrors = {};
    if (!isEdit && !formData.name.trim()) newErrors.name = "Group name is required.";
    if (!formData.description.trim()) newErrors.description = "Description is required.";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...(!isEdit && { name: formData.name }),
        description:             formData.description,
        notes:                   formData.notes,
        accountBalanceOutsideDF: "",
        existingLimitOutsideDF:  "",
        proposedLimitOutsideDF:  "",
        internalrisk:            "",
      };

      let resultId = groupId;
      if (isEdit) {
        await httpPatch(`/ExposureGroup/updategroup/${groupId}`, payload);
        dispatch(setToast({ type: toastSeverity.SUCCESS, message: STRINGS.EDIT_SUCCESS, open: true }));
      } else {
        const result = await httpPost(`/ExposureGroup/addgroup`, payload);
        resultId = result?.id || result?.Id || null;
        dispatch(setToast({ type: toastSeverity.SUCCESS, message: STRINGS.ADD_SUCCESS, open: true }));
      }

      if (onSaved) onSaved(resultId);
      if (!embedded) onClose();
    } catch (_) {
      dispatch(setToast({
        type: toastSeverity.ERROR,
        message: isEdit ? STRINGS.EDIT_ERROR : STRINGS.ADD_ERROR,
        open: true,
      }));
    } finally {
      setIsSaving(false);
    }
  }, [groupId, formData, errors, dispatch, onClose, onSaved, isEdit, embedded]);

  const isDisabled = isLoading || isSaving;
  const dialogTitle = isEdit ? STRINGS.EDIT_TITLE : STRINGS.ADD_TITLE;
  const saveLabel = isSaving ? "Saving…" : isEdit ? STRINGS.EDIT_SAVE : STRINGS.ADD_SAVE;

  const formContent = isLoading ? (
    <Box style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <CircularProgress size={32} />
    </Box>
  ) : (
    <Box>
      <Typography className={classes.sectionLabel}>Group</Typography>
      <Box className={classes.sectionCard}>
        <TextField
          id="name"
          label="Group Name"
          variant="outlined"
          size="small"
          fullWidth
          required={!isEdit}
          value={isEdit ? (groupName || "") : formData.name}
          onChange={isEdit ? undefined : handleChange}
          disabled={isEdit || isSaving}
          error={!isEdit && Boolean(errors.name)}
          helperText={!isEdit ? errors.name : undefined}
          className={classes.field}
        />
      </Box>

      {/* Description & Notes — always visible */}
      <Typography className={classes.sectionLabel}>Content</Typography>
      <Box className={classes.sectionCard}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              id="description"
              label={STRINGS.FIELD_DESCRIPTION}
              variant="outlined"
              size="small"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
              disabled={isSaving}
              error={Boolean(errors.description)}
              helperText={errors.description}
              className={classes.field}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="notes"
              label={STRINGS.FIELD_NOTES}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              disabled={isSaving}
              className={classes.field}
            />
          </Grid>
        </Grid>
      </Box>

    </Box>
  );

  // Embedded mode: rendered inside EntityMenuCustomForms's dialog — no shell needed
  if (embedded) {
    return (
      <Box>
        {formContent}
        <Box className={classes.embeddedActions}>
          <A_Button
            label="Cancel"
            premiumVariant="secondary"
            disabled={isDisabled}
            onClick={onClose}
          />
          <A_Button
            label={saveLabel}
            premiumVariant="primary"
            disabled={isDisabled}
            onClick={handleSave}
          />
        </Box>
      </Box>
    );
  }

  // Standalone mode: renders its own dialog (used from EGDetail)
  return (
    <A_SimpleDialog
      open={open}
      title={groupName ? `${dialogTitle} - ${groupName}` : dialogTitle}
      maxWidth="md"
      fullWidth
      disableBackdropClick={isDisabled}
      disableEscapeKeyDown={isDisabled}
      onClose={() => { if (!isDisabled) onClose(); }}
      dialogContent={formContent}
      dialogActions={
        <>
          <A_Button
            label="Cancel"
            premiumVariant="secondary"
            disabled={isDisabled}
            onClick={onClose}
          />
          <A_Button
            label={saveLabel}
            premiumVariant="primary"
            disabled={isDisabled}
            onClick={handleSave}
          />
        </>
      }
    />
  );
}

EGEditDialog.propTypes = {
  groupId:   PropTypes.string,
  groupName: PropTypes.string,
  open:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  onSaved:   PropTypes.func,
  isEdit:    PropTypes.bool,
  embedded:  PropTypes.bool,
};

EGEditDialog.defaultProps = {
  groupId:   null,
  groupName: "",
  onSaved:   null,
  isEdit:    true,
  embedded:  false,
};

export default EGEditDialog;
